<script lang="ts">
	import type { ComponentSectionsIntegrations, Product } from '$lib/graphql/strapiApi';
	import * as Card from '$lib/components/ui/card';
	import { buttonVariants } from './ui/button';
	import { cn } from '$lib/utils.js';
	import { ChevronRight } from 'lucide-svelte';
	import { mode } from '$lib/state/global.svelte';

	let { content }: { content: ComponentSectionsIntegrations } = $props();

	let activeCategory = $state('All');

	let exporeCategories = ['All', 'Recent', 'Coming Soon', 'Popular'];
</script>

<div class="m-auto w-full max-w-[1082px]">
	<div class="flex w-full flex-col">
		<h1 class="mb-4 text-4xl font-bold">{content.title}</h1>
		<p class="mb-4 text-xl">{content.description}</p>
		<div class="mt-4 flex flex-row gap-4 max-[536px]:flex-wrap">
			<div class="flex flex-col items-start">
				<h4 class="mb-2 text-xl font-bold">Explore</h4>
				{#each exporeCategories as category}
					<button
						class={cn(
							buttonVariants({
								variant: category === activeCategory ? 'default' : 'ghost'
							}),
							'w-full justify-start'
						)}
						onclick={() => {
							activeCategory = category;
						}}>{category}</button
					>
				{/each}
				<h4 class="my-2 text-xl font-bold">Categories</h4>
				{#each content.product_categories as category}
					{#if category?.name && category?.products?.filter((x) => x?.integration)?.length}
						<button
							class={cn(
								buttonVariants({
									variant: category?.name === activeCategory ? 'default' : 'ghost'
								}),
								'w-full justify-start'
							)}
							onclick={() => {
								if (category.name) {
									activeCategory = category?.name;
								}
							}}>{category.name}</button
						>
					{/if}
				{/each}
			</div>
			<div class="flex flex-row flex-wrap gap-4">
				{#each content.product_categories ?? [] as category}
					{#each category?.products ?? [] as product}
						{#if product?.integration && (activeCategory === category?.name || activeCategory === 'All' || (activeCategory === 'Coming Soon' && product.comingSoon))}
							{#if product.articles[0]?.slug}
								<a
									href={`/blog/${product.articles[0].slug}`}
									class="block transition-transform hover:scale-105"
								>
									<Card.Root class="h-72 w-72 hover:shadow-md transition-shadow cursor-pointer">
										<Card.Header class="flex h-[122px] flex-col">
											<div>
												{#if $mode === 'light'}
													<img
														class="mb-2 h-8 w-fit"
														src={product?.logo?.formats?.medium?.url ?? product.logo?.url}
														alt={product?.name}
													/>
												{:else}
													<img
														class="mb-2 h-8 w-fit"
														src={product?.darkLogo?.formats?.medium?.url ??
															product?.darkLogo?.url ??
															product?.logo?.formats?.medium?.url ??
															product?.logo?.url}
														alt={product?.name}
													/>
												{/if}
												<Card.Title class="flex items-center justify-between whitespace-nowrap text-xl">
													<h4>{product?.name}</h4>
												</Card.Title>
											</div>

											<div class="flex gap-2">
												<div
													class="w-fit rounded-md bg-gray-200 px-2.5 py-0.5 text-xs font-semibold text-black"
												>
													{category?.name}
												</div>
												{#if product?.comingSoon}
													<div
														class="w-fit rounded-md bg-orange-200 px-2.5 py-0.5 text-xs font-semibold text-black"
													>
														Coming Soon!
													</div>
												{/if}
											</div>
										</Card.Header>
										<Card.Content class="h-[116px] py-2">
											{#if product?.description}
												<Card.Description
													class="h-[96px] overflow-hidden text-ellipsis text-[13px] font-medium text-primary [&>a:hover]:underline [&>a]:font-semibold"
													>{@html product.description}</Card.Description
												>
											{/if}
										</Card.Content>
										<Card.Footer>
											<span
												class={cn(
													buttonVariants({ variant: 'secondary', size: 'sm' }),
													'items-center'
												)}>Learn More <ChevronRight /></span>
										</Card.Footer>
									</Card.Root>
								</a>
							{:else}
								<Card.Root class="h-72 w-72">
									<Card.Header class="flex h-[122px] flex-col">
										<a
											href={product.url}
											class={product.url ? 'hover:underline' : 'pointer-events-none'}
											target="_blank"
											rel="noopener noreferrer"
										>
											{#if $mode === 'light'}
												<img
													class="mb-2 h-8 w-fit"
													src={product?.logo?.formats?.medium?.url ?? product.logo?.url}
													alt={product?.name}
												/>
											{:else}
												<img
													class="mb-2 h-8 w-fit"
													src={product?.darkLogo?.formats?.medium?.url ??
														product?.darkLogo?.url ??
														product?.logo?.formats?.medium?.url ??
														product?.logo?.url}
													alt={product?.name}
												/>
											{/if}
											<Card.Title class="flex items-center justify-between whitespace-nowrap text-xl">
												<h4>{product?.name}</h4>
											</Card.Title>
										</a>

										<div class="flex gap-2">
											<div
												class="w-fit rounded-md bg-gray-200 px-2.5 py-0.5 text-xs font-semibold text-black"
											>
												{category?.name}
											</div>
											{#if product?.comingSoon}
												<div
													class="w-fit rounded-md bg-orange-200 px-2.5 py-0.5 text-xs font-semibold text-black"
												>
													Coming Soon!
												</div>
											{/if}
										</div>
									</Card.Header>
									<Card.Content class="h-[116px] py-2">
										{#if product?.description}
											<Card.Description
												class="h-[96px] overflow-hidden text-ellipsis text-[13px] font-medium text-primary [&>a:hover]:underline [&>a]:font-semibold"
												>{@html product.description}</Card.Description
											>
										{/if}
									</Card.Content>
								</Card.Root>
							{/if}
						{/if}
					{/each}
				{/each}
			</div>
		</div>
	</div>
</div>
